package fr.frux.feature.site.detail

import fr.frux.core.common.logging.Logger
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.SpeciesLookalike
import fr.frux.shared.domain.repository.SpeciesRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

/**
 * Référentiel des espèces d'une fiche de lieu, partagé par les sosies et la recherche d'une espèce
 * manquante. Chargé une fois par fiche : il ne change pas d'un geste à l'autre, et hors ligne son
 * appel réseau retarderait tout ce qui l'attend. Un échec laisse la prochaine demande réessayer.
 */
internal class SiteSpeciesReferential(
    private val speciesRepository: SpeciesRepository,
    private val scope: CoroutineScope,
    private val onLoaded: SiteSpeciesReferential.() -> Unit,
) {
    private var species: List<Species> = emptyList()
    private var loaded = false
    private var job: Job? = null

    fun load() {
        if (loaded || job?.isActive == true) return
        job =
            scope.launch {
                speciesRepository
                    .getSpecies()
                    .onSuccess {
                        species = it
                        loaded = true
                        onLoaded()
                    }.onFailure { Logger.d(TAG, "Référentiel espèces indisponible : ${it.message}") }
            }
    }

    /** Sosies par id d'espèce plantée, vide tant que le référentiel n'est pas là. */
    fun lookalikesOf(site: Site): Map<Long, List<SpeciesLookalike>> {
        val speciesIds = site.plantings.mapTo(mutableSetOf()) { it.species.id }
        if (speciesIds.isEmpty()) return emptyMap()
        return species
            .filter { it.id in speciesIds && it.lookalikes.isNotEmpty() }
            .associate { it.id to it.lookalikes }
    }

    fun search(
        query: String,
        excluded: Set<Long>,
        max: Int,
    ): List<Species> = species.filter { it.selectable && it.id !in excluded && it.matches(query) }.take(max)

    private companion object {
        const val TAG = "SiteDetailViewModel"
    }
}
